import { inject, Injectable, signal } from '@angular/core';
import { FirebaseMessaging, NotificationReceivedEvent } from '@capacitor-firebase/messaging';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { APP_CONFIG } from '@okcargo-apps/shared-infrastructure';
import { AccountCommandService } from '../account';

// TODO move outside?

export type PushNotificationPermissionStatus = 'granted' | 'denied' | 'unknown';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly config = inject(APP_CONFIG);
  private readonly storageService = inject(StorageRepository);
  private readonly accountCommandService = inject(AccountCommandService);

  private readonly _permissionStatus = signal<PushNotificationPermissionStatus>('unknown');
  public readonly permissionStatus = this._permissionStatus.asReadonly();

  private isListenersSetup = false;

  public async registerFcmToken(): Promise<void> {
    if (this.config.firebase) {
      try {
        const isSupported = await FirebaseMessaging.isSupported();
        if (!isSupported.isSupported) {
          console.warn('Push notifications are not supported in this browser or app');
          this._permissionStatus.set('unknown');
          return;
        }

        const permissionResult = await FirebaseMessaging.requestPermissions();

        if (permissionResult.receive === 'granted') {
          const tokenResult = await FirebaseMessaging.getToken({
            vapidKey: this.config.firebase.vapidKey,
          });

          if (tokenResult.token) {
            this.handleFcmToken(tokenResult.token);
            if (!this.isListenersSetup) {
              this.setupAllListeners();
              this.isListenersSetup = true;
            }
          }

          this._permissionStatus.set('granted');
        } else {
          console.warn('Push notification permission denied');
          this._permissionStatus.set('denied');
        }
      } catch (error) {
        console.error('Error registering FCM token:', error);
      }
    }
  }

  // TODO not in use for now, it is for check when user comes from background
  public async checkPermissionStatus(): Promise<PushNotificationPermissionStatus> {
    try {
      const result = await FirebaseMessaging.checkPermissions();
      const status: PushNotificationPermissionStatus = result.receive as PushNotificationPermissionStatus;
      this._permissionStatus.set(status);
      return status;
    } catch (error) {
      console.error('Error checking permission status:', error);
      this._permissionStatus.set('unknown');
      return 'unknown';
    }
  }

  // TODO not in use for now, use it when user logout
  public async deleteToken(): Promise<void> {
    try {
      await FirebaseMessaging.deleteToken();
      await this.storageService.delete('fcmToken');
      console.log('FCM token deleted successfully');
    } catch (error) {
      console.error('Error deleting FCM token:', error);
    }
  }

  private async handleFcmToken(token: string): Promise<void> {
    const storedToken = await this.storageService.read('fcmToken');

    if (storedToken.value !== token) {
      this.sendFCMToken(token);
      this.storageService.update('fcmToken', token);
    }
  }

  private setupAllListeners(): void {
    this.setupFcmTokenListener();
    this.setupForegroundNotificationListener();
    this.setupNotificationTapListener();
  }

  private setupFcmTokenListener(): void {
    FirebaseMessaging.addListener('tokenReceived', (event) => {
      this.handleFcmToken(event.token);
    });
  }

  // TODO partial implemented, only for test
  private setupForegroundNotificationListener(): void {
    FirebaseMessaging.addListener('notificationReceived', async (event: NotificationReceivedEvent) => {
      console.log('Notification received in foreground:', event);

      // const notification = event.notification;
    });
  }

  // TODO partial implemented, only for test
  private setupNotificationTapListener(): void {
    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('Notification action performed:', event);

      // const notification = event.notification;
    });
  }

  private sendFCMToken(token: string): Promise<void> {
    return this.accountCommandService.addPushTokenCommand({
      token: token,
    });
  }

  // TODO not implemented yet. Move to another service to manage when app returns from background
  // private setupAppStateListener(): void {
  //   App.addListener('appStateChange', async (state) => {
  //     if (state.isActive) {
  //       await this.checkPermissionStatus();
  //     }
  //   });
  // }

  // TODO not implemented yet.
  // private handleNotificationNavigation(notification: Notification): void {
  //   const data = notification.data;

  //   if (!data) {
  //     return;
  //   }

  //   if (data['route']) {
  //     // Si viene una ruta directa
  //     this.router.navigate([data['route']]);
  //   } else if (data['type']) {
  //     switch (data['type']) {
  //       case 'freight':
  //         if (data['freightId']) {
  //           this.router.navigate(['/freights', data['freightId']]);
  //         }
  //         break;

  //       default:
  //         console.log('Unknown notification type:', data['type']);
  //     }
  //   }
  // }
}
